import React, { useState, useEffect } from 'react';

interface Team {
  TeamId: string;
  TeamName: string;
  DepartmentId: string;
  TeamLeadEmail: string;
}

interface Department {
  DepartmentId: string;
  DepartmentName: string;
}

export default function TeamManager() {
  const [teams, setTeams] = useState<Team[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ teamName: '', departmentId: '', teamLeadEmail: '' });
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDepartments();
    fetchTeams();
  }, []);

  const fetchDepartments = async () => {
    try {
      const res = await fetch('/departments');
      const data = await res.json();
      setDepartments(data.data || []);
    } catch (err) {
      console.error('Failed to fetch departments:', err);
    }
  };

  const fetchTeams = async () => {
    try {
      setLoading(true);
      const res = await fetch('/teams');
      const data = await res.json();
      setTeams(data.data || []);
    } catch (err) {
      console.error('Failed to fetch teams:', err);
    } finally {
      setLoading(false);
    }
  };

  const getDepartmentName = (departmentId: string) => {
    const dept = departments.find(d => d.DepartmentId === departmentId);
    return dept?.DepartmentName || 'Unknown';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.departmentId) {
      setError('Please select a department');
      return;
    }
    try {
      const res = await fetch('/teams', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          teamName: formData.teamName,
          departmentId: formData.departmentId,
          teamLeadEmail: formData.teamLeadEmail,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setFormData({ teamName: '', departmentId: '', teamLeadEmail: '' });
        setShowForm(false);
        setError('');
        fetchTeams();
      } else {
        setError(data.error || 'Failed to create team');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create team');
    }
  };

  return (
    <div style={{ marginBottom: '2rem' }}>
      <h2>Team Management</h2>
      {departments.length === 0 && (
        <div style={{ backgroundColor: '#fff3cd', border: '1px solid #ffc107', color: '#856404', padding: '1rem', borderRadius: '4px', marginBottom: '1rem' }}>
          <strong>Note:</strong> Create departments first, then add teams to them.
        </div>
      )}
      <button
        className="btn-primary"
        onClick={() => setShowForm(!showForm)}
        style={{ marginBottom: '1rem' }}
        disabled={departments.length === 0}
      >
        {showForm ? 'Cancel' : '+ Add Team'}
      </button>

      {showForm && (
        <form onSubmit={handleSubmit} style={{ background: '#f9f9f9', padding: '1rem', borderRadius: '4px', marginBottom: '1rem' }}>
          {error && <div style={{ color: '#dc3545', marginBottom: '1rem' }}>{error}</div>}
          <div className="form-group">
            <label>Team Name</label>
            <input
              type="text"
              value={formData.teamName}
              onChange={(e) => setFormData({ ...formData, teamName: e.target.value })}
              placeholder="e.g., Engineering Team"
              required
              style={{ width: '100%', padding: '0.5rem', marginBottom: '0.5rem' }}
            />
          </div>
          <div className="form-group">
            <label>Department</label>
            <select
              value={formData.departmentId}
              onChange={(e) => setFormData({ ...formData, departmentId: e.target.value })}
              required
              style={{ width: '100%', padding: '0.5rem', marginBottom: '0.5rem' }}
            >
              <option value="">-- Select Department --</option>
              {departments.map(dept => (
                <option key={dept.DepartmentId} value={dept.DepartmentId}>
                  {dept.DepartmentName}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Team Lead Email</label>
            <input
              type="email"
              value={formData.teamLeadEmail}
              onChange={(e) => setFormData({ ...formData, teamLeadEmail: e.target.value })}
              placeholder="lead@example.com"
              required
              style={{ width: '100%', padding: '0.5rem', marginBottom: '0.5rem' }}
            />
          </div>
          <button type="submit" className="btn-primary">Create Team</button>
        </form>
      )}

      <div>
        <h3>Teams ({teams.length})</h3>
        {teams.length === 0 ? (
          <p style={{ color: '#666' }}>No teams yet</p>
        ) : (
          <div style={{ display: 'grid', gap: '1rem' }}>
            {teams.map(team => (
              <div key={team.TeamId} style={{ background: 'white', padding: '1rem', borderRadius: '4px', border: '1px solid #ddd' }}>
                <h4>{team.TeamName}</h4>
                <p style={{ margin: '0.5rem 0', color: '#666', fontSize: '0.9rem' }}>
                  <strong>Department:</strong> {getDepartmentName(team.DepartmentId)}
                </p>
                <p style={{ margin: '0.5rem 0', color: '#666', fontSize: '0.9rem' }}>
                  <strong>Lead:</strong> {team.TeamLeadEmail}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
